import { Action, Subject } from '@erp/authz';
import type {
  DraftPurchaseOrderFreight,
  FreightOptionRow,
  ListFreightCodesResponse,
} from '@erp/rpc';
import { useCan } from '@/hooks/useCan';
import {
  useDraftFreight,
  useFreightCodes,
  useFreightOptions,
} from 'src/api/rpc/hooks/procurement/useFreightOptions';
import { pickableOptions } from './draftFreightFormat';

export interface DraftFreightPanel {
  freight: DraftPurchaseOrderFreight;
  shipViaCodes: ListFreightCodesResponse['shipVia'];
  freightTermsCodes: ListFreightCodesResponse['freightTerms'];
  options: FreightOptionRow[];
  /** The PO is not sent, the reader may edit POs, and the branch has options. */
  canEdit: boolean;
  canReadOptions: boolean;
}

/** The panel's state from what was loaded and what the reader may do. */
export function draftFreightPanel(params: {
  freight: DraftPurchaseOrderFreight | undefined;
  codes: ListFreightCodesResponse | undefined;
  options: FreightOptionRow[] | undefined;
  canUpdatePo: boolean;
  canReadOptions: boolean;
}): DraftFreightPanel | null {
  const { freight } = params;
  if (!freight) return null;
  const options = pickableOptions(params.options ?? [], freight.supplierId);
  return {
    freight,
    shipViaCodes: params.codes?.shipVia ?? [],
    freightTermsCodes: params.codes?.freightTerms ?? [],
    options,
    canEdit: freight.editable && params.canUpdatePo && options.length > 0,
    canReadOptions: params.canReadOptions,
  };
}

/** Loads a draft PO's freight with the branch's codes and options. */
export function useDraftFreightPanel(
  draftPurchaseOrderId: string,
): DraftFreightPanel | null {
  const [canUpdatePo] = useCan(Action.Update, Subject.PurchaseOrder);
  const [canReadOptions] = useCan(Action.Read, Subject.FreightOption);
  const freight = useDraftFreight(draftPurchaseOrderId).data;
  const warehouseId = canReadOptions ? (freight?.warehouseId ?? null) : null;
  const codes = useFreightCodes(warehouseId).data;
  const options = useFreightOptions({
    warehouseId: warehouseId ?? undefined,
    enabled: warehouseId !== null,
  }).data?.options;
  return draftFreightPanel({
    freight,
    codes,
    options,
    canUpdatePo,
    canReadOptions,
  });
}
